import type { BasicChatAuthContext, Schema, SchemaData } from '@lemonade-hq/chat-engine';
import { ma, SessionStatus } from '@lemonade-hq/chat-engine';
import type { Nullish } from '@lemonade-hq/ts-helpers';
import { createContext, useContext } from 'react';
import type { ChatEngineInitSessionRequestPayload } from './chat_engine.client';

export type WizardExtendedStepWithPersistence<TWizardStep = unknown> = TWizardStep & {
  readonly name: string;
  readonly persistedAnswers?: SchemaData;
};

export type WizardExtendedStepWithPersistenceAndId<TWizardStep = unknown> =
  WizardExtendedStepWithPersistence<TWizardStep> & {
    readonly id: string;
  };

export interface WizardBasePersistenceContextValue<TWizardStep = unknown> {
  readonly steps: WizardExtendedStepWithPersistenceAndId<TWizardStep>[];
  readonly answersSchema: Schema;
  readonly persistedAnswersByStep: SchemaData[];
  readonly isInitializing: boolean;
  readonly isSubmitting: boolean;
  readonly assets: string[];
  readonly error?: Nullish | boolean | string;
  readonly sessionStatus?: SessionStatus;
  readonly sessionId?: string;
  readonly truncateObsoleteLocalSteps: (newCurrentStepIndex: number) => void;
  readonly auth?: BasicChatAuthContext;
}

export interface WizardPersistenceContextSubmissionOptions {
  readonly resync?: boolean;
  readonly skipHooks?: boolean;
}

export interface WizardPersistenceLoadSessionProps extends ChatEngineInitSessionRequestPayload {
  readonly initialStepName?: string;
  readonly serverBaseUrl: string;
  readonly sessionId?: string | null;
  readonly accessTokenOverride?: string | null;
  readonly emailCode?: string | null;
  readonly serverTimeoutOverride?: number;
  readonly clearPreviousSession?: boolean;
}

export interface WizardPersistenceContextValue<TWizardStep = unknown>
  extends WizardBasePersistenceContextValue<TWizardStep> {
  readonly submitAnswers: (
    answers: SchemaData,
    options?: WizardPersistenceContextSubmissionOptions,
  ) => Promise<{ readonly history: WizardExtendedStepWithPersistence<TWizardStep>[]; readonly success: boolean }>;
  readonly initSession: (props: WizardPersistenceLoadSessionProps) => void;
  readonly isSubmittingLLMRequest: boolean;
  readonly isErrorFromLLMRequest: boolean;
}

export const WizardPersistenceContext = createContext<WizardPersistenceContextValue>({
  submitAnswers: async () => ({
    history: [],
    success: false,
  }),
  steps: [],
  answersSchema: ma.record({}),
  persistedAnswersByStep: [],
  truncateObsoleteLocalSteps: () => {},
  isInitializing: false,
  isSubmitting: false,
  assets: [],
  sessionStatus: SessionStatus.Pending,
  error: null,
  isSubmittingLLMRequest: false,
  isErrorFromLLMRequest: false,
  initSession: () => {},
});

export function useWizardPersistenceContext<TWizardStep = unknown>(): WizardPersistenceContextValue<TWizardStep> {
  return useContext(WizardPersistenceContext) as WizardPersistenceContextValue<TWizardStep>;
}
